import { createContext, useContext, useMemo } from 'react';

import AntdConfigProvider from '@/components/helper/AntdConfigProvider';
import { DASHBOARD } from '@/constants/routes';
import useColorScheme from '@/hooks/useColorScheme';
import { defaultTheme } from '@/theme/buildAntdTheme';
import { resolveHirePortTheme } from '@/theme/resolveHirePortTheme';

import type { HirePortTheme } from '@/theme/buildAntdTheme';
import type { ColorScheme } from '@/theme/colorScheme';
import type { PackName } from '@/theme/partnerThemes';
import type { ReactNode } from 'react';

export type HirePortContextType = {
  theme: HirePortTheme;
  /** Resolved light/dark scheme (not the user's system/light/dark preference). */
  colorScheme: ColorScheme;
  renderPageWrapper: boolean;
  renderPageHeader: boolean;
  urlBase: string;
  /**
   * In-app home for this surface. `null` on embed (no Client dashboard).
   * NotFound only offers a home CTA when this is set.
   */
  homePath: string | null;
  /** When set, message navigation uses this instead of `/messages` (e.g. embed agency inbox). */
  messagesPathPrefix?: string;
  renderCustom: {
    recruiterPublicProfile?: {
      hideCandidateList: boolean;
    };
    recruiterSelection?: {
      disableLinkToProfile: boolean;
    };
    jobEdit?: {
      hideProgress: boolean;
    };
  };
  sessionLocale?: string;
  atsType?: PackName;
  atsPostMessageStrategy?: PackName;
};

const defaultHireportContext: HirePortContextType = {
  theme: defaultTheme,
  colorScheme: 'light',
  renderPageWrapper: true,
  renderPageHeader: true,
  urlBase: '',
  homePath: DASHBOARD,
  renderCustom: {},
};

const EMPTY_PARTIAL: Partial<HirePortContextType> = {};

const HirePortContext = createContext(defaultHireportContext);

const useHirePortContext = () => {
  return useContext(HirePortContext);
};

export const HirePortContextProvider = ({
  value = EMPTY_PARTIAL,
  children,
}: {
  value?: Partial<HirePortContextType>;
  children: ReactNode;
}) => {
  const previousContext = useHirePortContext();
  const { resolvedScheme } = useColorScheme();
  const themeOverride = value.theme;
  const colorScheme = value.colorScheme ?? resolvedScheme;

  const resolvedTheme = useMemo(() => {
    const merged = themeOverride
      ? { ...previousContext.theme, ...themeOverride }
      : previousContext.theme;
    return resolveHirePortTheme(merged, colorScheme);
  }, [previousContext.theme, themeOverride, colorScheme]);

  const newContext = useMemo(
    (): HirePortContextType => ({
      ...previousContext,
      ...value,
      theme: resolvedTheme,
      colorScheme,
    }),
    [previousContext, resolvedTheme, value, colorScheme],
  );

  return (
    <HirePortContext.Provider value={newContext}>
      <AntdConfigProvider>{children}</AntdConfigProvider>
    </HirePortContext.Provider>
  );
};

export default useHirePortContext;
